/**
 * Kuuraii Global Lazy Media Controller
 * Handles site-wide on-demand video loading, viewport playback observers,
 * background lazy-loading, mute/unmute audio toggling, and asynchronous image decoding.
 */
(() => {
  'use strict';

  const SVG_SOUND_ICONS = `
    <svg class="kr-video-sound__icon kr-video-sound__icon--muted" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M11 5L6 9H2v6h4l5 4V5z"/>
      <line x1="23" y1="9" x2="17" y2="15"/>
      <line x1="17" y1="9" x2="23" y2="15"/>
    </svg>
    <svg class="kr-video-sound__icon kr-video-sound__icon--sound" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M11 5L6 9H2v6h4l5 4V5z"/>
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
    </svg>
  `;

  // 1. Promote data-src to src and reload video element
  const loadVideoElement = (video) => {
    if (!video || video.dataset.loaded === 'true') return;

    const sources = video.querySelectorAll('source[data-src]');
    if (sources.length > 0) {
      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();
    } else if (video.dataset.src) {
      video.src = video.dataset.src;
      video.removeAttribute('data-src');
      video.load();
    }

    video.dataset.loaded = 'true';
  };

  // Expose globally for instant on-demand playback across PDP and collection scripts
  window.KuuraiiLazyMedia = {
    loadVideo: loadVideoElement
  };

  // 2. Helper to find container for video and sound toggle
  const getVideoContainer = (video) => {
    return (
      video.closest(
        '.quick-product__media--story-video, .bedding-artisan-story__media, .bedding-loom-visual__video, .bedding-inline-video, .making-video, .toda-video-card, .food-landing__media'
      ) || video.parentElement
    );
  };

  // 3. Ensure a sound toggle button exists for every video
  const ensureSoundToggleButton = (video) => {
    const container = getVideoContainer(video);
    if (!container) return null;

    let button = container.querySelector('[data-video-sound-toggle]');
    if (!button) {
      button = document.createElement('button');
      button.type = 'button';
      button.className = 'kr-video-sound-toggle';
      button.dataset.videoSoundToggle = 'true';
      button.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
      button.setAttribute('title', video.muted ? 'Unmute video' : 'Mute video');
      button.innerHTML = SVG_SOUND_ICONS;
      container.appendChild(button);
    }

    // Sync initial state
    button.classList.toggle('is-unmuted', !video.muted);
    return button;
  };

  // 4. Observer for videos: preloads before viewport, autoplays muted in viewport, pauses offscreen
  const initVideoObserver = () => {
    const selector =
      'video[data-lazy-video], video[data-autoplay-in-view], .quick-product-video-card__video, .bedding-artisan-story__video, .bedding-inline-video__media, .making-video video';
    const allVideos = document.querySelectorAll(selector);
    if (!allVideos.length) return;

    allVideos.forEach((video) => {
      // Default to muted autoplay configuration for smooth mobile & desktop playback
      if (video.muted === undefined || video.muted === false) {
        video.muted = true;
      }
      video.playsInline = true;
      video.loop = true;

      // Sync state changes with sound toggle button and container classes
      const container = getVideoContainer(video);
      ensureSoundToggleButton(video);

      if (!video.dataset.lazyMediaBound) {
        video.dataset.lazyMediaBound = 'true';

        video.addEventListener('volumechange', () => {
          const btn = container?.querySelector('[data-video-sound-toggle]');
          if (btn) {
            btn.classList.toggle('is-unmuted', !video.muted && video.volume > 0);
            const label = video.muted || video.volume === 0 ? 'Unmute video' : 'Mute video';
            btn.setAttribute('aria-label', label);
            btn.setAttribute('title', label);
          }
        });

        video.addEventListener('play', () => {
          container?.classList.add('is-playing');
          const cardBtn = container?.querySelector('.quick-product-video-card');
          if (cardBtn) cardBtn.classList.add('is-playing');
        });

        video.addEventListener('pause', () => {
          container?.classList.remove('is-playing');
          const cardBtn = container?.querySelector('.quick-product-video-card');
          if (cardBtn) cardBtn.classList.remove('is-playing');
        });
      }
    });

    if ('IntersectionObserver' in window) {
      // Preload observer: promotes data-src -> src 300px before video reaches screen
      const preloadObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadVideoElement(entry.target);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '300px 0px', threshold: 0 }
      );

      allVideos.forEach((video) => preloadObserver.observe(video));

      // Autoplay observer: plays videos when actually visible in viewport
      const autoplayObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            const container = getVideoContainer(video);

            if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
              loadVideoElement(video);
              // Always guarantee muted before autoplaying so browsers don't reject
              if (video.dataset.userUnmuted !== 'true') {
                video.muted = true;
              }
              const playPromise = video.play();
              if (playPromise !== undefined) {
                playPromise.catch(() => {});
              }
            } else if (!entry.isIntersecting || entry.intersectionRatio < 0.1) {
              if (!video.paused) {
                video.pause();
              }
            }
          });
        },
        { threshold: [0, 0.1, 0.2] }
      );

      allVideos.forEach((video) => autoplayObserver.observe(video));
    } else {
      // Fallback: eager load & play if no IntersectionObserver support
      allVideos.forEach((video) => {
        loadVideoElement(video);
        video.muted = true;
        video.play().catch(() => {});
      });
    }
  };

  // 5. Sound toggle click listener (delegated across entire document)
  const handleSoundToggleClicks = (event) => {
    const toggleBtn = event.target.closest('[data-video-sound-toggle]');
    if (!toggleBtn) return;

    event.preventDefault();
    event.stopPropagation();

    const container = toggleBtn.closest(
      '.quick-product__media--story-video, .bedding-artisan-story__media, .bedding-loom-visual__video, .bedding-inline-video, .making-video, .toda-video-card, .food-landing__media, article'
    );
    const video = container?.querySelector('video');
    if (!video) return;

    loadVideoElement(video);

    const willUnmute = video.muted;

    if (willUnmute) {
      // Mute all other videos on the page so audio doesn't overlap
      document.querySelectorAll('video').forEach((other) => {
        if (other !== video) {
          other.muted = true;
          delete other.dataset.userUnmuted;
          const otherContainer = getVideoContainer(other);
          const otherBtn = otherContainer?.querySelector('[data-video-sound-toggle]');
          if (otherBtn) {
            otherBtn.classList.remove('is-unmuted');
            otherBtn.setAttribute('aria-label', 'Unmute video');
            otherBtn.setAttribute('title', 'Unmute video');
          }
        }
      });

      video.muted = false;
      video.dataset.userUnmuted = 'true';
      toggleBtn.classList.add('is-unmuted');
      toggleBtn.setAttribute('aria-label', 'Mute video');
      toggleBtn.setAttribute('title', 'Mute video');

      if (video.paused) {
        video.play().catch(() => {});
      }
    } else {
      video.muted = true;
      delete video.dataset.userUnmuted;
      toggleBtn.classList.remove('is-unmuted');
      toggleBtn.setAttribute('aria-label', 'Unmute video');
      toggleBtn.setAttribute('title', 'Unmute video');
    }
  };

  // 6. Delegate click-to-play / click-to-pause on video cards
  const handlePlayClicks = (event) => {
    // If user clicked sound toggle, do nothing here
    if (event.target.closest('[data-video-sound-toggle]') || event.target.closest('a')) {
      return;
    }

    const trigger = event.target.closest(
      '[data-collection-video-card], [data-bedding-story-play], [data-making-video-play], [data-toda-click-video], [data-video-modal-trigger]'
    );
    if (!trigger) return;

    const container = trigger.closest(
      'article, .quick-product__media, .bedding-artisan-story__media, .bedding-loom-visual__video, .making-video, .toda-video-card, .food-landing__media'
    );
    const video = container?.querySelector('video');
    if (!video) return;

    loadVideoElement(video);

    if (video.paused) {
      video.play().catch(() => {});
      container?.classList.add('is-playing');
    } else {
      video.pause();
      container?.classList.remove('is-playing');
    }
  };

  // 7. Ensure async decoding on all images
  const ensureAsyncImages = () => {
    document.querySelectorAll('img:not([decoding])').forEach((img) => {
      img.decoding = 'async';
    });
  };

  // 8. Initialize on load & Shopify section re-renders
  const init = () => {
    initVideoObserver();
    ensureAsyncImages();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Bind delegated listeners with capture phase for sound toggle so it always intercepts reliably
  document.addEventListener('click', handleSoundToggleClicks, true);
  document.addEventListener('click', handlePlayClicks);
  document.addEventListener('shopify:section:load', init);
})();
